import {
  DEFAULT_SCOPE,
  DEFAULT_STATUS,
  deliveryChannelOptions,
  NOT_SPECIFIED_RESPONSE_TYPE,
  type IpawsDeliveryChannel,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/Ipaws.config';
import { truncateAreaGeometryCoordinates } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/coordinatePrecision.utils';
import type {
  FemaAlertArea,
  FemaAlertInfo,
  FemaAlertPayload,
  FemaCategory,
  FemaEasOrgCode,
  FemaResponseType,
} from 'types/fema.types';
import { FEMA_EAS_ORG_CODES } from 'types/fema.types';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;
const cleanValueCodes = (codes?: FemaValueCodeInput[]) =>
  (codes ?? []).filter((code) => isNonEmptyString(code.valueName) && isNonEmptyString(code.value));

export type IpawsMessageContent = {
  environment?: string;
  language?: string;
  event?: string;
  eventCode?: string;
  /** CAP allows several categories per <info>; the whole list is sent together. */
  category?: FemaCategory[];
  responseType?: (FemaResponseType | typeof NOT_SPECIFIED_RESPONSE_TYPE)[];
  urgency?: FemaAlertInfo['urgency'];
  severity?: FemaAlertInfo['severity'];
  certainty?: FemaAlertInfo['certainty'];
  contact?: string;
  web?: string;
  instruction?: string;
  areaDescription?: string;
  /** Operator-selected pathways. WEA uses its OASIS profile value, `CMAS`, on the wire. */
  deliveryChannels?: readonly IpawsDeliveryChannel[];
  /**
   * The COG profile's EAS organization code from `GET /fusion/v2/fema/jurisdictions` (decision
   * D18). A code: it is the one `EAS-ORG` written. `null`: the profile has none, so no `EAS-ORG` is
   * written and the validator refuses EAS and NWEM. Omitted: the caller has no profile answer (a
   * saved Global Action template), so the one valid `EAS-ORG` in `parameters` is kept.
   */
  easOrgCode?: FemaEasOrgCode | null;
  parameters?: FemaValueCodeInput[];
  geocodes?: FemaValueCodeInput[];
  resources?: FemaResourceInput[];
  languages?: IpawsMessageLanguage[];
};

export type FemaValueCodeInput = {
  valueName: string;
  value: string;
};

export type FemaResourceInput = {
  resourceDesc: string;
  mimeType: string;
  uri?: string;
  size?: number;
};

export type IpawsMessageLanguage = {
  language: string;
  event?: string;
  headline?: string;
  description?: string;
  instruction?: string;
  areaDescription?: string;
  web?: string;
  contact?: string;
  weaShortText?: string;
  weaLongText?: string;
  isReviewed?: boolean;
  resources?: FemaResourceInput[];
};

export type FemaPayloadInput = {
  status?: FemaAlertPayload['status'];
  messageType?: FemaAlertPayload['msgType'];
  scope?: FemaAlertPayload['scope'];
  restriction?: string;
  addresses?: string[];
  references?: FemaAlertPayload['references'];
  issuedTime?: string;
  onsetTime?: string;
  expirationTime?: string;
  headline?: string;
  description?: string;
  content?: IpawsMessageContent;
  areaGeometry?: Pick<FemaAlertArea, 'polygon' | 'circle'>;
};

const BLOCK_CHANNEL_PARAMETER_NAME = 'BLOCKCHANNEL';
const DELIVERY_CHANNEL_VALUES = deliveryChannelOptions.map(({ value }) => value);

const isFemaEasOrgCode = (value: string): value is FemaEasOrgCode =>
  FEMA_EAS_ORG_CODES.some((code) => code === value);

/**
 * CAP has no delivery-channel element. FEMA's repeatable BLOCKCHANNEL parameter removes every
 * unchecked IPAWS pathway; an omitted selection preserves callers that already provide their own
 * channel parameters. `EAS-ORG` is written once, for EAS or NWEM, from `easOrgCode` (see
 * `IpawsMessageContent.easOrgCode`). It is never hard-coded (decision D18).
 */
export const buildIpawsDeliveryChannelParameters = (
  parameters?: FemaValueCodeInput[],
  deliveryChannels?: readonly IpawsDeliveryChannel[],
  easOrgCode?: FemaEasOrgCode | null,
): FemaValueCodeInput[] => {
  const cleanedParameters = cleanValueCodes(parameters);
  if (!Array.isArray(deliveryChannels)) return cleanedParameters;

  const selectedChannels = new Set(deliveryChannels);
  const nonChannelParameters = cleanedParameters.filter(
    ({ valueName }) => valueName !== BLOCK_CHANNEL_PARAMETER_NAME && valueName !== 'EAS-ORG',
  );
  const draftEasOrgCodes = cleanedParameters
    .filter(({ valueName }) => valueName === 'EAS-ORG')
    .map(({ value }) => value.trim());
  const keptDraftEasOrgCode =
    draftEasOrgCodes.length === 1 && isFemaEasOrgCode(draftEasOrgCodes[0])
      ? draftEasOrgCodes[0]
      : null;
  const writtenEasOrgCode = easOrgCode === undefined ? keptDraftEasOrgCode : easOrgCode;
  const generatedParameters =
    writtenEasOrgCode && (selectedChannels.has('EAS') || selectedChannels.has('NWEM'))
      ? [{ valueName: 'EAS-ORG', value: writtenEasOrgCode }]
      : [];
  const blockedChannelParameters = DELIVERY_CHANNEL_VALUES.filter(
    (channel) => !selectedChannels.has(channel),
  ).map((channel) => ({ valueName: BLOCK_CHANNEL_PARAMETER_NAME, value: channel }));

  return [...nonChannelParameters, ...generatedParameters, ...blockedChannelParameters];
};

/**
 * Builds the alert object sent to the backend.
 * The backend adds trusted sender and sent-time details, so this function does not accept them
 * from the form. Missing operator choices also stay missing so validation can ask for them instead
 * of silently guessing.
 */
export const buildFemaAlertPayload = (input: FemaPayloadInput): FemaAlertPayload => {
  const ipawsData = input.content ?? {};

  // Every CAP coordinate is truncated to at most 4 decimal places (FEMA advisory 627).
  const areaGeometry = truncateAreaGeometryCoordinates(input.areaGeometry ?? {});
  const hasAreaGeometry = Boolean(areaGeometry.polygon?.length || areaGeometry.circle?.length);
  const addresses = input.addresses ?? [];
  const categories = (ipawsData.category ?? []).filter(isNonEmptyString);
  const responseTypes = (ipawsData.responseType ?? []).filter(
    (responseType): responseType is FemaResponseType =>
      isNonEmptyString(responseType) && responseType !== NOT_SPECIFIED_RESPONSE_TYPE,
  );
  const geocodes = cleanValueCodes(ipawsData.geocodes);
  const parameters = buildIpawsDeliveryChannelParameters(
    ipawsData.parameters,
    ipawsData.deliveryChannels,
    ipawsData.easOrgCode,
  );

  // Each language uses the same map shapes but may provide its own area description.
  const buildArea = (areaDesc: string | undefined) =>
    areaDesc || hasAreaGeometry || geocodes.length
      ? [
          {
            areaDesc: areaDesc ?? '',
            ...areaGeometry,
            ...(geocodes.length ? { geocode: geocodes } : {}),
          },
        ]
      : undefined;

  const primaryInfo: FemaAlertInfo = {
    category: categories,
    event: ipawsData.event ?? '',
    ...(responseTypes.length ? { responseType: responseTypes } : {}),
    urgency: ipawsData.urgency ?? 'Unknown',
    severity: ipawsData.severity ?? 'Unknown',
    certainty: ipawsData.certainty ?? 'Unknown',
    ...(isNonEmptyString(ipawsData.contact) ? { contact: ipawsData.contact.trim() } : {}),
    ...(isNonEmptyString(ipawsData.web) ? { web: ipawsData.web.trim() } : {}),
    eventCode: ipawsData.eventCode ? [{ valueName: 'SAME', value: ipawsData.eventCode }] : [],
    effective: input.issuedTime || undefined,
    onset: input.onsetTime || undefined,
    expires: input.expirationTime || '',
    headline: input.headline ?? '',
    description: input.description ?? '',
    instruction: ipawsData.instruction || undefined,
    parameter: parameters,
    resource: (ipawsData.resources ?? []).filter(
      (resource) => isNonEmptyString(resource.resourceDesc) && isNonEmptyString(resource.mimeType),
    ),
    area: buildArea(ipawsData.areaDescription),
    language: ipawsData.language || undefined,
  };

  // Extra languages reuse shared alert details and map shapes, but provide their own public text.
  const additionalLanguageInfo = (ipawsData.languages ?? [])
    .filter((language) => isNonEmptyString(language.language))
    .map((language): FemaAlertInfo => {
      const languageParameters = (primaryInfo.parameter ?? []).filter(
        ({ valueName }) => valueName !== 'CMAMtext' && valueName !== 'CMAMlongtext',
      );
      if (language.weaShortText)
        languageParameters.push({ valueName: 'CMAMtext', value: language.weaShortText });
      if (language.weaLongText)
        languageParameters.push({ valueName: 'CMAMlongtext', value: language.weaLongText });
      return {
        ...primaryInfo,
        language: language.language.trim(),
        event: language.event ?? primaryInfo.event,
        web: isNonEmptyString(language.web) ? language.web.trim() : primaryInfo.web,
        contact: isNonEmptyString(language.contact) ? language.contact.trim() : primaryInfo.contact,
        headline: language.headline ?? '',
        description: language.description ?? '',
        instruction: language.instruction || undefined,
        parameter: languageParameters,
        // A language block carries only its own resources. The English EAS audio placeholder has
        // no bytes, and no part fills it in a Spanish block, so it is never copied (2026-10-08).
        resource: (language.resources ?? []).filter(
          (resource) =>
            isNonEmptyString(resource.resourceDesc) && isNonEmptyString(resource.mimeType),
        ),
        area: buildArea(language.areaDescription ?? ipawsData.areaDescription),
      };
    });

  return {
    status: input.status ?? DEFAULT_STATUS,
    msgType: input.messageType ?? 'Alert',
    scope: input.scope ?? DEFAULT_SCOPE,
    restriction: input.restriction || undefined,
    ...(addresses.length ? { addresses } : {}),
    references: input.references?.length ? input.references : undefined,
    info: [primaryInfo, ...additionalLanguageInfo],
  };
};
